import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Callout, Chip, Drawer, FormField, Input, Select, useToast } from '@/components/ui';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { updateAppRole } from '@/api/console';
import type { AppRoleConversion } from './types';

export interface EditAppRoleDrawerProps {
    role: AppRoleConversion | null;
    onClose: () => void;
}

/**
 * EAA-1817 (frame 48): "Sửa vai trò của ứng dụng". Editable as of EAA-1709
 * (gap G9) shipping `PATCH /admin/app-roles/{id}` — the prior read-only
 * version was correct at the time it was written (the endpoint genuinely had
 * no detail path or PATCH then), not a design deviation.
 *
 * Only `name` and `permissions` are submitted — `key`/`is_active`/`convertedTo`
 * render as plain text, mirroring `AppRoleService.EDITABLE_FIELDS` exactly, so
 * the form never implies a field is editable when the server would silently
 * ignore it.
 */
export function EditAppRoleDrawer({ role, onClose }: EditAppRoleDrawerProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState<string[]>([]);

    // Reset every time the drawer targets a different role — a half-edited
    // name for one role means nothing once the operator opens another.
    useEffect(() => {
        if (!role) return;
        setName(role.name);
        setPermissions(role.permissions);
    }, [role?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const mutation = useMutation({
        mutationFn: () =>
            updateAppRole(config.apiBase, accessToken as string, (role as AppRoleConversion).id, {
                name: name.trim(),
                permissions,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['admin-app-roles'] });
            toast.success(t('console.appDetail.roleDrawer.saveSuccess'));
            onClose();
        },
        onError: () => {
            toast.error(t('console.appDetail.roleDrawer.saveError'));
        },
    });

    const dirty = role !== null && (name.trim() !== role.name || permissions.join() !== role.permissions.join());
    const canSave = role !== null && name.trim().length > 0 && dirty;

    return (
        <Drawer
            title={
                <div className="app-detail-drawer-title">
                    <span>{t('console.appDetail.roleDrawer.title')}</span>
                    {role && <code>{role.key}</code>}
                </div>
            }
            open={Boolean(role)}
            onClose={onClose}
        >
            {role && (
                <div className="app-detail-drawer-body">
                    <FormField label={t('console.appDetail.roleDrawer.fieldName')}>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </FormField>

                    <FormField
                        label={t('console.appDetail.roleDrawer.fieldPermissions')}
                        hint={t('console.appDetail.roleDrawer.permissionsHint')}
                    >
                        {/* Free-text tags, not a picker from a catalog: this edits
                            portal-api's own cached snapshot of what the app already
                            reported, not the app's live permission set — there is no
                            endpoint exposing valid codes to validate against. */}
                        <Select
                            mode="tags"
                            value={permissions}
                            onChange={(value) => setPermissions(value as string[])}
                            tokenSeparators={[',']}
                            style={{ width: '100%' }}
                        />
                    </FormField>

                    <div className="app-detail-fieldlist__row">
                        <dt>{t('console.appDetail.roleDrawer.fieldConvertedTo')}</dt>
                        <dd>
                            <div className="app-detail-chip-wrap">
                                {role.convertedTo.length > 0 ? (
                                    role.convertedTo.map((roleKey) => <Chip key={roleKey}>{roleKey}</Chip>)
                                ) : (
                                    <Chip tone="unmapped">
                                        {t('console.appDetail.roles.unconvertedChip')}
                                    </Chip>
                                )}
                            </div>
                        </dd>
                    </div>
                    <div className="app-detail-fieldlist__row">
                        <dt>{t('console.appDetail.roleDrawer.fieldSource')}</dt>
                        <dd>
                            {!role.isActive
                                ? t('console.appDetail.roles.sourceRemoved')
                                : role.isSynced
                                  ? t('console.appDetail.roles.sourceSynced')
                                  : t('console.appDetail.roles.sourceManual')}
                        </dd>
                    </div>

                    {!role.isActive && (
                        <Callout tone="warning" label={t('console.appDetail.roleDrawer.removedCalloutLabel')}>
                            {t('console.appDetail.roleDrawer.removedCalloutBody')}
                        </Callout>
                    )}

                    <div className="app-detail-drawer-actions">
                        <Button onClick={onClose} disabled={mutation.isPending}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => mutation.mutate()}
                            disabled={!canSave || mutation.isPending}
                        >
                            {mutation.isPending
                                ? t('console.appDetail.roleDrawer.saving')
                                : t('console.appDetail.roleDrawer.saveButton')}
                        </Button>
                    </div>
                </div>
            )}
        </Drawer>
    );
}

export default EditAppRoleDrawer;
